import type { Options as KyOptions } from 'ky';
import ky from 'ky';
import { AUTH_ENDPOINT_KEY, CONTENT_TYPE_HEADER } from '../../auth/const';
import handleMobileCookies from '../native/handleMobileCookies';
import isNative from '../native/isNative';
import { isCorsMode } from './credentials';
import { clearDpopKeyOnLogout } from './hooks/afterResponse/dpopKey';
import { handleExpiredPassword } from './hooks/afterResponse/expiredPassword';
import { handleLogging } from './hooks/afterResponse/logger';
import { handleLogout } from './hooks/afterResponse/logout';
import { getLogBeforeErrorHook } from './hooks/beforeError/logError';
import { getDeltaMatrixHeaders } from './hooks/beforeRequest/deltaMatrix';
import { getDpopHeaders } from './hooks/beforeRequest/dpop';
import { getInterfaceHeaders } from './hooks/beforeRequest/interface';
import { getInternalSvcRequest } from './hooks/beforeRequest/internalSvc';
import { getMetadataHeaders } from './hooks/beforeRequest/metadata';
import getMobileAuthHeaders from './hooks/beforeRequest/mobileAuth';
import { getProjectHeaders } from './hooks/beforeRequest/project';
import { getServerAuthHeaders } from './hooks/beforeRequest/serverAuth';
import { getLogBeforeRetryHook } from './hooks/beforeRetry/logRetry';
import { getRefreshTokenHook } from './hooks/beforeRetry/refreshToken';
import type { FetchArgs, FetchOptions } from './types';
import { UAFetchError } from './UAFetchError';
import { getServerFetch } from './unpatchedFetch';
import { getBaseURL, getQueryParams, getResponseData, isServer } from './utils';
import { compressJsonData } from './utils/compression';

// references:
// https://github.com/suhaotian/xior/blob/e46dfab26f5a4ddb73928d9e1b15b7f83576d930/src/xior.ts#L225
// https://github.com/elbywan/wretch/blob/d2cb52235026c3a68f8a9949f08d99234c6675be/src/core.ts#L95

// Backstop timeout (ms) for server-side upstream fetches.
//
// Why this exists: on the Node server we route ky through the *unpatched* fetch
// (see unpatchedFetch) to avoid Next's patched-fetch response-tee leak. But Next's
// patched fetch is also what tears down in-flight fetches when the request ends,
// so bypassing it drops that teardown. When a client disconnects mid-SSR (a tab
// closed, a navigation away, a proxy/LB timeout — routine under real traffic), the
// upstream fetch the render had started is never cancelled. Its socket (pinned
// natively via TLSWrap/WriteWrap) and the SSR render closure chain are retained
// for good. Each orphaned request leaks ~15KB of JS heap; under sustained traffic
// they accumulate, linear and unbounded, until the process OOMs. This is the
// matrix-global symptom: nodejs_heap_size_used_bytes climbs to 4GB over ~40h while
// external_memory stays flat (a pure JS-heap leak, distinct from the off-heap tee
// leak above). A finite timeout reaps these orphaned fetches.
//
// 60s is generous enough not to affect legitimate SSR (the platform request
// timeout sits below it); override via UA_SERVER_FETCH_TIMEOUT_MS for tuning.
// Applied on the Node server only — client and native keep ky's default (no
// timeout), matching prior behaviour.
const SERVER_FETCH_TIMEOUT_MS = Number(process.env.UA_SERVER_FETCH_TIMEOUT_MS) || 60_000;

export const executeFetch = async <T>(
  fetchArgs: FetchArgs,
  options: FetchOptions = { returnRawResponse: false },
): Promise<T> => {
  if (!options.meta) {
    // TODO: log this to sentry if called from within interface?
    // console.log('fetch.ts: meta not defined ', { fetchArgs, options });
  }
  const { url, method, params, headers = {}, data, signal, rawBody } = fetchArgs;
  if (!isServer() && (options.meta?.apiDomain ?? isCorsMode())) {
    options.credentials = 'include';
  }

  const baseURL = await getBaseURL({ url, options });
  const queryParams = getQueryParams({ data, params }, options);

  const hookArgs = { fetchArgs, options, baseURL, executeFetch };
  const logout = options.meta?.logout;
  const requestHeaders: Record<string, string> = {
    ...headers,
    ...options.headers,
    ...options.meta?.headers,
  };

  // Handle JSON data compression
  const { stringifiedJson, compressedBody, shouldCompress, compressionType } =
    await compressJsonData(data);

  if (shouldCompress) {
    requestHeaders['Content-Encoding'] = compressionType;
  }

  // Determine json and body options
  let jsonOption: unknown;
  let bodyOption: BodyInit | null | undefined;

  if (shouldCompress) {
    bodyOption = compressedBody;
  } else if (data instanceof FormData) {
    bodyOption = data;
  } else if (rawBody) {
    bodyOption = rawBody;
  } else if (!(data instanceof FormData)) {
    jsonOption = data;
  }

  // Skip 401 retry for auth endpoints since the user is not logged in yet,
  // making refresh token calls unnecessary and causing duplicate requests
  // Also skip for a public interface: it's sessionless, so a 401 has no token to
  // refresh and must not trigger the refresh→logout path.
  const shouldRetryOn401 =
    isNative() && !url.includes(AUTH_ENDPOINT_KEY) && !options.meta?.isPublicInterface;

  const kyOptions: KyOptions = {
    method,
    credentials: options.credentials,
    onDownloadProgress: options.onDownloadProgress,
    headers: requestHeaders,
    stringifyJson: stringifiedJson ? () => stringifiedJson : undefined,
    searchParams: queryParams,
    json: jsonOption,
    body: bodyOption,
    signal,
    // Server/Node-only backstop reaping upstream fetches orphaned by client
    // disconnects mid-SSR (see SERVER_FETCH_TIMEOUT_MS). Off elsewhere.
    timeout: isServer() && !isNative() ? SERVER_FETCH_TIMEOUT_MS : false,
    retry: shouldRetryOn401
      ? {
          statusCodes: [401],
          limit: 1,
          methods: ['get', 'put', 'head', 'delete', 'options', 'trace', 'post'],
        }
      : undefined,
    cache: options.cache,
    hooks: {
      beforeRequest: [
        getMetadataHeaders(hookArgs),
        getServerAuthHeaders(hookArgs),
        getMobileAuthHeaders(hookArgs),
        getDeltaMatrixHeaders(hookArgs),
        getProjectHeaders(hookArgs),
        getInterfaceHeaders(hookArgs),
        getInternalSvcRequest,
        getDpopHeaders(hookArgs),
      ],
      afterResponse: isNative()
        ? [clearDpopKeyOnLogout, handleLogging(hookArgs)]
        : [
            clearDpopKeyOnLogout,
            handleLogout(hookArgs),
            handleExpiredPassword(hookArgs),
            handleLogging(hookArgs),
          ],
      beforeRetry: isNative()
        ? [getLogBeforeRetryHook(hookArgs), getRefreshTokenHook(hookArgs, logout)]
        : undefined,
      beforeError: [getLogBeforeErrorHook(options)],
    },
  };

  // On the Node server (not RN), route ky through the native fetch captured
  // before Next patched globalThis.fetch (see unpatchedFetch). This bypasses
  // Next's patched-fetch response tee, which leaks memory under SSR load. Inert
  // (falls back to the default fetch) when no capture exists — client, native, or
  // a server that has not opted in via instrumentation.
  if (isServer() && !isNative()) {
    const unpatchedFetch = getServerFetch();
    if (unpatchedFetch) {
      kyOptions.fetch = unpatchedFetch;
    }
  }

  const response = await ky(baseURL, kyOptions).catch((e: unknown) => {
    // A 401 during SSR means the request is unauthenticated, typically a stale or expired `_at`
    // cookie. The server render must produce the logged-out state rather than crash. Throwing here
    // aborts the RSC render, and because pending queries are dehydrated for streaming, the rejection
    // of an in-flight auth query surfaces as a fatal render error. Resolve empty instead so the query
    // succeeds with no data. The client still throws and redirects on 401 (see handleLogout, which
    // already no-ops on the server).
    if (e instanceof UAFetchError && e.status === 401 && isServer()) {
      return null;
    }
    // UAFetchError is already handled by beforeError hook
    if (!(e instanceof UAFetchError)) {
      console.error(`[fetch internal] error: ${(e as Error).message}`, { url });
    }
    throw e;
  });

  if (response === null) {
    return null as T;
  }

  if (response.ok && response.status === 204) {
    return {} as T;
  }

  try {
    if (response.headers.get('set-cookie')) {
      handleMobileCookies(response.headers.get('set-cookie'));
    }
    const responseData = await getResponseData(response, options);
    return responseData as T;
  } catch (e) {
    const contentType = response.headers.get(CONTENT_TYPE_HEADER) ?? '';
    console.error(`Failed to parse response for ${contentType}`, {
      url,
      statusText: response.statusText,
      status: response.status,
    });

    return response as T;
  } finally {
    // enable on demand
    // if (process.env.NODE_ENV === 'development') {
    //   console.debug('Response', {
    //     // duration: measure.duration,
    //     url,
    //     status: response.status,
    //     statusText: response.statusText,
    //     params,
    //     payload: data,
    //   });
    // }
  }
};

export type ErrorType<T> = UAFetchError<T>;
